// src/components/shell/WorkspaceModeBanners.tsx
// Per-mode banners and helpers that used to live in LeadWorkspaceHeader*.
// The chrome (title/tagline/bell/mode pill/sign-out/gear/help) has moved to
// TopStrip, Sidebar, and the new HelpPopover; this component preserves the
// mode-scoped content that wasn't moved (profile completion badge, lead
// status, send banners, queued count, profile editor, KB gap nudge). The
// Location Links card moved to the sidebar in Slice A (see
// docs/planning/activity_dock_workflow_facilitation.md §6) — the
// `showLocationLinks` open-state plumbing went with it. Later slices will
// fold the remaining items into their respective per-mode panels.
import { useAuth } from "@/contexts/AuthContext";
import {
  canAccessPrimaryMode,
  type PrimaryMode,
} from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { OrgLocation } from "@/config/locations";
import type { Location } from "@/types/location";
import { LeadsModeBanners } from "./LeadsModeBanners";

type Salesperson = { name: string; cell: string; email: string };

export type WorkspaceModeBannersProps = {
  mode: PrimaryMode;

  location: Location | null;
  setLocation: (l: Location) => void;

  lastSendAt: number | null;
  lastSendStatus: "success" | "error" | null;

  queuedCount: number;

  salesperson: Salesperson | null;
  setSalesperson: (s: Salesperson | null) => void;
  showProfileEditor: boolean;

  locationsLoaded: boolean;
  locations: OrgLocation[];
  refreshLocations: (reason?: string, opts?: { force?: boolean }) => void;

  voiceEnabled: boolean;
  setVoiceEnabled: (v: boolean) => void;
  voiceProvider: "system" | "custom";
  setVoiceProvider: (v: "system" | "custom") => void;
  speakAnswers: boolean;
  canSpeakHelp: boolean;
  setSpeakMode: (m: "answers" | "help" | "all") => void;

  kbGapOpenCount?: number | null;
  onOpenKbGapInbox?: () => void;
};

export function WorkspaceModeBanners(props: WorkspaceModeBannersProps) {
  const auth = useAuth();
  const { mode, kbGapOpenCount = null, onOpenKbGapInbox } = props;

  if (mode.startsWith("mfg")) return null;

  const hasRequiredProfileName =
    !!auth.profile?.full_name?.trim() && /^[A-Za-z]+\s+[A-Za-z]+/.test(auth.profile.full_name.trim());
  const hasRequiredProfileEmail = !!auth.profile?.email?.trim();
  const hasRequiredProfilePhone = !!(auth.profile?.personal_phone?.trim() || auth.profile?.work_phone?.trim());
  const shouldPromptForAccountProfile =
    !!auth.session &&
    !!auth.profile &&
    (!hasRequiredProfileName || !hasRequiredProfileEmail || !hasRequiredProfilePhone);

  const authCanAccessKnowledgebase = canAccessPrimaryMode("knowledgebase", auth);

  return (
    <div className="w-full">
      {shouldPromptForAccountProfile ? (
        <div className="mb-3 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs sm:text-sm text-amber-900">
          Please complete your user profile in Settings → Profile.
        </div>
      ) : null}

      {mode === "leads" ? (
        <LeadsModeBanners
          location={props.location}
          setLocation={props.setLocation}
          lastSendAt={props.lastSendAt}
          lastSendStatus={props.lastSendStatus}
          queuedCount={props.queuedCount}
          salesperson={props.salesperson}
          setSalesperson={props.setSalesperson}
          showProfileEditor={props.showProfileEditor}
          locationsLoaded={props.locationsLoaded}
          locations={props.locations}
          refreshLocations={props.refreshLocations}
          voiceEnabled={props.voiceEnabled}
          setVoiceEnabled={props.setVoiceEnabled}
          voiceProvider={props.voiceProvider}
          setVoiceProvider={props.setVoiceProvider}
          speakAnswers={props.speakAnswers}
          canSpeakHelp={props.canSpeakHelp}
          setSpeakMode={props.setSpeakMode}
        />
      ) : null}

      {authCanAccessKnowledgebase && kbGapOpenCount != null && kbGapOpenCount > 0 ? (
        <div className="mb-4 text-left rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/35 dark:text-amber-50">
          <div className="font-semibold">KB gaps need to be addressed</div>
          <p className="mt-1 text-xs text-amber-900/90 dark:text-amber-100/90">
            There {kbGapOpenCount === 1 ? "is" : "are"} {kbGapOpenCount} open gap inbox{" "}
            {kbGapOpenCount === 1 ? "item" : "items"}. Review them in Knowledge Base → Gap Inbox.
          </p>
          {onOpenKbGapInbox ? (
            <button
              type="button"
              className="mt-2 inline-flex items-center rounded-lg border border-amber-500 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/40 dark:text-amber-50 dark:hover:bg-amber-900/60"
              onClick={() => onOpenKbGapInbox()}
            >
              Open Gap Inbox
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
